import React from 'react';
import { useAuthStore } from './features/auth/authStore';

export const DebugAuth: React.FC = () => {
  const authState = useAuthStore();
  const { isAuthenticated, user, tenant, reinitializeAuth } = authState;

  // Get localStorage data
  const localStorageData = localStorage.getItem('erpca-auth');
  let parsedLocalStorage = null;
  try {
    parsedLocalStorage = localStorageData ? JSON.parse(localStorageData) : null;
  } catch (e) {
    console.error('Error parsing localStorage:', e);
  }

  // Test role checking logic
  const requiredRoles = ['SuperAdmin'];
  const userRoles = Array.isArray(user?.roles) ? user.roles : [];
  const hasRequiredRole = requiredRoles.some(role => userRoles.includes(role));

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8">🔍 Auth Debug Information</h1>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Auth Store State */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Auth Store State</h2>
            <div className="space-y-2 text-sm">
              <div><strong>isAuthenticated:</strong> {String(isAuthenticated)}</div>
              <div><strong>user:</strong> {user ? 'Present' : 'null'}</div>
              {user && (
                <>
                  <div><strong>user.email:</strong> {user.email}</div>
                  <div><strong>user.roles:</strong> {JSON.stringify(user.roles)}</div>
                  <div><strong>user.roles type:</strong> {typeof user.roles}</div>
                  <div><strong>user.roles isArray:</strong> {String(Array.isArray(user.roles))}</div>
                </>
              )}
              <div><strong>tenant:</strong> {tenant ? tenant.name : 'null'}</div>
            </div>
          </div>

          {/* localStorage Data */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">localStorage Data</h2>
            <div className="text-sm">
              {parsedLocalStorage ? (
                <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto">
                  {JSON.stringify(parsedLocalStorage, null, 2)}
                </pre>
              ) : (
                <div>No localStorage data found</div>
              )}
            </div>
          </div>

          {/* Role Check Test */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Role Check Test</h2>
            <div className="space-y-2 text-sm">
              <div><strong>Required Roles:</strong> {JSON.stringify(requiredRoles)}</div>
              <div><strong>User Roles:</strong> {JSON.stringify(userRoles)}</div>
              <div><strong>Has Required Role:</strong> 
                <span className={hasRequiredRole ? 'text-green-600' : 'text-red-600'}>
                  {String(hasRequiredRole)}
                </span>
              </div>
              <div><strong>Should Allow Admin Access:</strong> 
                <span className={hasRequiredRole ? 'text-green-600' : 'text-red-600'}>
                  {hasRequiredRole ? 'YES' : 'NO'}
                </span>
              </div>
            </div>
          </div>

          {/* Manual Role Check */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold mb-4">Manual Role Check</h2>
            <div className="space-y-2 text-sm">
              {requiredRoles.map(role => {
                const hasRole = userRoles.includes(role);
                return (
                  <div key={role}>
                    <strong>"{role}":</strong> 
                    <span className={hasRole ? 'text-green-600' : 'text-red-600'}>
                      {hasRole ? ' ✅ Found' : ' ❌ Not Found'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={() => {
              console.log('🔍 Full auth state:', authState);
              console.log('🔍 localStorage:', parsedLocalStorage);
              console.log('🔍 Role check result:', hasRequiredRole);
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Log to Console
          </button>

          <button
            onClick={() => {
              reinitializeAuth();
              setTimeout(() => window.location.reload(), 100);
            }}
            className="px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-600"
          >
            Reinitialize Auth
          </button>

          <button
            onClick={() => {
              // Mock SuperAdmin login for testing
              const mockAuthData = {
                user: {
                  id: '507f1f77bcf86cd799439011',
                  email: 'superadmin@erpca.ca',
                  firstName: 'Super',
                  lastName: 'Admin',
                  roles: ['SuperAdmin'],
                  status: 'active',
                  tenantId: null
                },
                tenant: null,
                accessToken: 'mock-token',
                refreshToken: 'mock-refresh-token',
                isAuthenticated: true
              };

              localStorage.setItem('erpca-auth', JSON.stringify(mockAuthData));
              useAuthStore.setState(mockAuthData);
              console.log('🧪 Mock SuperAdmin login set');
              window.location.reload();
            }}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            Mock SuperAdmin Login
          </button>

          <button
            onClick={() => window.location.href = '/admin'}
            className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600"
          >
            Try Admin Access
          </button>

          <button
            onClick={() => window.location.href = '/dashboard'}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};
